import { Link } from '@tanstack/react-router';
import { Bot, CalendarClock, Mail, MessageSquare, NotebookPen, Phone, Send, Users, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { MESSAGE_STATUSES, TIMELINE_FILTERS, TIMELINE_FILTER_LABELS, type TimelineFilter, type TimelineItem } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { MESSAGE_STATUS_TONES, TASK_STATUS_TONES } from '../../../components/status-tones';
import { EmptyState, FilteredEmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDate, formatDateTimeLong, formatRelative } from '../../../lib/format';
import { useTimeline, type TimelineSubject } from '../api/communication-api';
import { ComposePanel } from './ComposePanel';
import { QuickLogBar } from './QuickLogBar';
import styles from '../Communication.module.css';

const ICONS: Readonly<Record<TimelineItem['type'], LucideIcon>> = {
  call: Phone,
  email: Mail,
  sms: MessageSquare,
  note: NotebookPen,
  meeting: Users,
  system: Bot,
};

const STATUS_LABELS: Readonly<Record<string, string>> = {
  queued: 'Sending',
  scheduled: 'Waiting to send',
  cancelled: 'Not sent',
  failed: 'Failed',
  bounced: 'Bounced',
};

/**
 * COMM-01 — everything that happened with a person, newest first: what people logged, every
 * email that went to them and every one they sent back, and what the system did — each kind
 * filterable. The quick-log bar (COMM-02) and "Message" (COMM-03) sit on top when allowed.
 */
export function Timeline({ subject, title = 'Activity' }: { readonly subject: TimelineSubject; readonly title?: string }): ReactNode {
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const timeline = useTimeline(subject, filter);
  const { anchor, triggerRef, toggle, close } = usePopover();
  const first = timeline.data?.pages[0];
  const items = timeline.data?.pages.flatMap((page) => page.items) ?? [];
  const can = first?.can ?? { log: false, message: false };
  const target = subject.kind === 'application' ? { applicationId: subject.id, candidateId: null } : { applicationId: null, candidateId: subject.kind === 'candidate' ? subject.id : null };
  const attempts = first?.contactAttempts ?? 0;

  return (
    <Card
      title={title}
      meta={attempts > 0 ? `${attempts} contact ${attempts === 1 ? 'attempt' : 'attempts'} logged` : undefined}
      action={
        can.message ? (
          <Button ref={triggerRef} variant="outline" size="sm" icon={Send} onClick={toggle}>
            Message
          </Button>
        ) : undefined
      }
    >
      <div className={styles.timeline}>
        {can.log && subject.kind !== 'job' && <QuickLogBar target={target} />}
        <div className={styles.filters}>
          <Segmented label="Show" value={filter} options={TIMELINE_FILTERS.map((value) => ({ value, label: TIMELINE_FILTER_LABELS[value] }))} onChange={setFilter} />
        </div>
        {timeline.error && !timeline.data && timeline.error.status === 403 ? (
          // The server refuses this reader on purpose (§L23); a quiet note, never a generic error.
          <p className={styles.meta}>Activity isn't available to you.</p>
        ) : timeline.error && !timeline.data ? (
          <ServerErrorState onRetry={() => void timeline.refetch()} />
        ) : !timeline.data ? (
          <SkeletonLines count={5} />
        ) : items.length === 0 ? (
          filter === 'all' ? (
            <EmptyState isInset title="Nothing yet" body={can.log ? 'Log a call or a note above, or send a message.' : 'Calls, emails and notes will collect here.'} action={null} />
          ) : (
            <FilteredEmptyState filterCount={1} onClearFilters={() => setFilter('all')} />
          )
        ) : (
          <ul className={styles.items}>
            {items.map((item) => (
              <TimelineRow key={item.id} item={item} showApplication={subject.kind !== 'application'} />
            ))}
          </ul>
        )}
        {timeline.hasNextPage && (
          <Button variant="ghost" isPending={timeline.isFetchingNextPage} onClick={() => void timeline.fetchNextPage()}>
            Show older
          </Button>
        )}
      </div>
      <Popover anchor={anchor} onClose={close} align="right" width={460} label="Message">
        <ComposePanel target={target} onCancel={close} onDone={close} />
      </Popover>
    </Card>
  );
}

function TimelineRow({ item, showApplication }: { readonly item: TimelineItem; readonly showApplication: boolean }): ReactNode {
  const Icon = ICONS[item.type];
  const messageStatus = MESSAGE_STATUSES.find((status) => status === item.messageStatus);
  const messageLabel = messageStatus ? STATUS_LABELS[messageStatus] : undefined;
  return (
    <li className={styles.item}>
      <span className={`${styles.icon} ${item.isSystem ? styles.iconSystem : ''}`} aria-hidden>
        <Icon size={14} />
      </span>
      <div className={styles.itemBody}>
        <span className={styles.itemTitle}>{item.title}</span>
        <span className={styles.meta} title={formatDateTimeLong(item.at)}>
          {[item.isSystem ? 'Automated' : item.actorName, formatRelative(item.at)].filter(Boolean).join(' · ')}
          {showApplication && item.application && (
            <>
              {' · '}
              <Link to="/applications/$applicationId" params={{ applicationId: item.application.id }} className={styles.name}>
                {item.application.jobTitle}
              </Link>
            </>
          )}
        </span>
        {item.body && <p className={styles.itemText}>{item.body}</p>}
        {(messageStatus && messageLabel) || item.task ? (
          <span className={styles.chips}>
            {messageStatus && messageLabel && (
              <Chip tone={MESSAGE_STATUS_TONES[messageStatus]} plain>
                {messageLabel}
              </Chip>
            )}
            {item.task && (
              <Chip tone={TASK_STATUS_TONES[item.task.status]} plain>
                <CalendarClock size={12} aria-hidden /> {item.task.status === 'done' ? 'Follow-up done' : `Follow-up ${item.task.dueAt ? formatDate(item.task.dueAt) : ''}`}
              </Chip>
            )}
          </span>
        ) : null}
      </div>
    </li>
  );
}
